import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { departmentsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface DepartmentImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DepartmentImportSheet({ open, onOpenChange }: DepartmentImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import departments"
      description="Upload an .xlsx or .csv."
      onImport={async (file, { idempotencyKey }) => {
        const res = await departmentsApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          unchanged: res.data.unchanged,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            // Pass structured errors straight through — the generic
            // ImportSheet renders `{ code, message, field }` objects
            // with a field chip next to each message.
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.departments.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await departmentsApi.exportTemplate();
          triggerDownload(blob, filename ?? 'departments-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}
